import type { Snapshot } from '../data/data.ts';
// The rail (SPEC-redesign §5.4, §5.5, §8). `src/derive/axis.ts` decides what the
// rows are, `src/axis/rows.ts` builds them, and this file decides where in the
// list they go and what the scroll does around them.
//
// Two rules shape everything here. Selection is state, not an event (§4.7): a
// row and its mark highlight each other because both read `viewState.selected`.
// And the axis is the keyboard path through the data (D-f): every row is a
// button in time order, each one followed by its voice tag.
import { atNowOrAfter, axisNow, foldOld } from '../derive/axis.ts';
import type { Derived } from '../derive/derive.ts';
import { derived, type DerivedState } from '../derived.ts';
import { haversine } from '../shared/geo.ts';
import type { Item } from '../shared/model.ts';
import { mount } from '../shell.ts';
import { gesture } from '../state/gesture.ts';
import { viewState, type ViewState } from '../state/viewstate.ts';
import {
  anchorOf,
  KRESKA_ROW,
  placeAtNow,
  replaceRows,
  rowFor,
  scrollForAnchor,
  scrollRowIntoView,
  scrollTopOf,
  setScroll,
} from './anchor.ts';
import type { DetailContext } from './detail.ts';
import { focusedNow, restoreFocus } from './focus.ts';
import { applyLive, clearLive, playFlip, settleFlip } from './live-rows.ts';
import { holdThroughResize } from './rail-resize.ts';
import { el, emptyRingRow, FOLD_ROW, foldRow, type Nearest, rowElement } from './rows.ts';
import { orderWasAsked } from './toolbar.ts';

/**
 * The nearest thing outside the ring, for the empty-ring sentence (§2.6).
 * `beyond` is already nearest first, so this is its head — measured again here
 * because the derivation carries the SET and not the distances.
 */
/**
 * What a detail card needs about the row it is opening: how far away it is, the
 * hour to read by, and the one ZTM notice that speaks for that vehicle's line,
 * so the card can thread it.
 */
function contextFor(
  item: Item,
  state: { snapshot: Snapshot; view: ViewState; centre: { lat: number; lon: number }; now: number },
): DetailContext {
  const { snapshot, view, centre, now } = state;
  const notice =
    item.kind === 'vehicle'
      ? (snapshot.items.find(
          (n) => n.kind === 'notice' && (item.lines ?? []).some((l) => (n.lines ?? []).includes(l)),
        ) ?? null)
      : null;
  return {
    metres: item.position ? haversine(centre, item.position) : null,
    now,
    notice,
    lines: view.lines,
    onlyLine: view.onlyLine,
  };
}

function nearestOutside(axis: Derived, centre: { lat: number; lon: number }): Nearest | null {
  const first = axis.beyond[0];
  if (!first?.position) return null;
  return { title: first.title, metres: haversine(centre, first.position), position: first.position };
}

/** How long the rail still belongs to the loading app rather than to the resident. */
const LOAD_MS = 2000;

export function mountAxis(): void {
  const rail = mount('rail');
  const list = el('ol', 'axis');
  rail.append(list);

  // The kreska is a row of this list at the „teraz" boundary, not a band above
  // it: past rows sit over it, future rows under it, and it pins to the rail's
  // top edge while the resident looks forward (§5.3). The shell hands it over
  // still empty and S3 fills it — this list is its only home from here on, and
  // the one owner of the list is the one that moves it.
  const kreska = el('li', KRESKA_ROW);
  // It is an `<li>` because an `<ol>` may hold nothing else, and the reading
  // order past rows → band → future rows is the whole point of putting it here.
  // But it is not one of the entries: a screen reader announcing „lista, 70
  // elementów" for 69 rows miscounts what there is to read, and the count is
  // how a resident judges that. `presentation` drops this element's own
  // `listitem` and nothing else — the group „Środek i teraz" and all four
  // controls inside it stay in the tree, which `aria-hidden` would not.
  kreska.setAttribute('role', 'presentation');
  kreska.append(mount('kreska'));
  list.append(kreska);

  // The standing derivation this rail draws: the rows, the snapshot, the view
  // and centre they were built for, and the one clock of that change
  // (`src/derived.ts`, ADR-0016). A render that is not a change — the fold
  // being opened — redraws this value rather than deriving against a second clock.
  let state: DerivedState = derived.get();
  // The selection the last render drew, so the next tells an opening from a
  // redraw. Seeded EMPTY: a `sel` the address arrives with is an opening too,
  // and leaving the rail where it was is #225 — 70 px below its window.
  let shown: string | null = null;
  // Whether the old past is open, and which hour the rail was last positioned
  // for. Both are this rail's own: neither is in the address, and a poll moves
  // neither.
  let foldOpen = false;
  let placedFor: string | null | undefined;
  // Set by the fold and read by the very next render: opening „starsze" asks to
  // see the past, so that rebuild puts the fold's own row at the rail's top
  // rather than holding what was there (#183).
  let foldMoved = false;

  function render() {
    const { derived: axis, snapshot, view, centre, now: realNow } = state;
    if (!axis || !snapshot) return;
    // Under the finger the rail does not rebuild, and a poll that lands
    // mid-gesture does not make it (§4.1): its snapshot stands in
    // `src/derived.ts` and the settle's one rebuild reads it. Distances and
    // membership move; the ORDER does not, which a rebuild here would change.
    if (gesture.active()) {
      const live = gesture.get();
      if (live) applyLive(list, live, view.order);
      return;
    }
    // A gesture that hid rows is over; the rebuild below starts from a list
    // nobody has touched, so what it draws is the derivation's own answer.
    clearLive(list);
    // §4.2's FLIP, or `null` where §8 of SPEC-redesign and §4.2 say there is none.
    const flip = settleFlip(list, view.order);
    const now = axisNow(view, realNow);
    const moved = view.at != null;
    // Time is the default and is never switched automatically (§2.3). By
    // distance the kreska is the list's HEAD rather than a boundary, so nothing
    // stands above it and „starsze" goes to the very end.
    const byTime = view.order === 'czas';

    // A poll answers every ten seconds and rebuilds the list. Nobody walking the
    // rail by keyboard should lose their place to that, so the row that had
    // focus is found again by its id afterwards (§8).
    const focused = focusedNow(list, kreska);

    // The old past folds at the head, and „teraz" is what the rail opens on.
    const fold = foldOld(axis.rows, view, now);
    const drawn = fold.label && !foldOpen ? fold.rest : axis.rows;

    // Two fragments, because the kreska stands between them. In time order the
    // fold is always in the first: „starsze · N wpisów" is the head of the past
    // and stays the first thing on the rail even when nothing else of the past
    // is drawn. By distance it is the last row of all (§2.3).
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    const folded = fold.label
      ? foldRow(fold.label, foldOpen, () => {
          foldOpen = !foldOpen;
          foldMoved = true;
          render();
        })
      : null;
    if (folded && byTime) before.append(folded);
    // The kreska's own place in the list: the first row that is not behind us.
    // A row reading „teraz" is one of those — it is what the resident opens the
    // app for — and `side` cannot say so, because it puts `at === now` in the
    // past along with everything before it. `atNowOrAfter` is the rule the row's
    // own time column reads, so what the band divides is what the rows say
    // (issue 160, G1). The same pass decides which of the two fragments a row lands
    // in, so the boundary is never counted twice: the row that carries
    // `data-now` is the first one under the kreska, and a day with nothing ahead
    // of it leaves the kreska at the foot of the list.
    let boundary = byTime;
    for (const row of drawn) {
      const li = rowElement(row, view, { now, moved }, (item) =>
        contextFor(item.item, { snapshot, view, centre, now }),
      );
      if (boundary && atNowOrAfter(row.kind === 'item' ? row.item.at : row.at, now)) {
        li.dataset.now = 'true';
        boundary = false;
      }
      (boundary ? before : after).append(li);
    }
    // Nothing positioned inside the ring: one sentence in place of the rows,
    // directly under the kreska, with the placeless city-wide rows still listed
    // under it — they are not „in the ring" and not outside it (§2.6).
    if (!axis.placed.length) after.prepend(emptyRingRow(view.radius, nearestOutside(axis, centre)));
    if (folded && !byTime) after.append(folded);
    // Rebuilding the list drops the scroll position, and the feeds answer one at
    // a time — four snapshots in the first second, a fifth every ten. The
    // resident's place in the rail survives all of them; only the two deliberate
    // moves below may change it.
    //
    // „The same scroll" is not the same place. The rows above the band are what
    // decides where the band sits in the content, and a rebuild changes them:
    // a voice comes back, a snapshot brings more of the past, an event crosses
    // „teraz" and moves from under the band to over it. Putting the number back
    // verbatim left the band 3214 px down the rail on an un-mute (#183). What is
    // put back is the RELATIONSHIP the resident could see — the band at the top
    // edge, or the row they were reading at its own height on the screen.
    //
    const box = list.parentElement;
    const anchor = box ? anchorOf(box, list, kreska, byTime) : null;
    replaceRows(list, kreska, before, after);
    // Opening „starsze" is the one rebuild the resident asked for by name: the
    // old past they just unfolded goes where they can read it, its first row
    // directly under the row they pressed. Folding it again is the same move
    // backwards, and leaves them on the same row.
    const foldHere = foldMoved;
    foldMoved = false;
    // A re-order asked for BY NAME puts the rail at the top, like the „starsze"
    // fold (§5.3's exception); arriving on an order moves nothing. A distance
    // list still OPENS there: „teraz" is not a place on it (§2.3).
    const reordered = orderWasAsked() || (!byTime && placedFor === undefined);
    if (box && reordered) setScroll(box, () => 0);
    else if (box && anchor) {
      // A function rather than a number, because what to scroll to is not
      // known until the new rows are in the document: `reserveTail` above has
      // just settled the list's height, and the boundary this is measured
      // against moved with the rows that were replaced.
      setScroll(box, () => {
        const unfolded = foldHere ? list.querySelector<HTMLElement>(`.${FOLD_ROW}`) : null;
        return unfolded ? scrollTopOf(box, unfolded) : scrollForAnchor(box, list, kreska, anchor);
      });
    }

    // After the scroll is settled, so „first" and „last" are read in the same
    // content space and the restoration is not carried into every row's flight.
    if (flip) playFlip(list, flip);

    restoreFocus(list, focused);

    // An opened row is brought into view — from the row itself or from its mark
    // on the circle (§4.7) — and **only when the selection has just changed**.
    // A poll answers every ten seconds and a radius drag writes the view; doing
    // this on every render took the rail back 386 px twice a minute for as long
    // as a card was open, so a resident who scrolled away from the row they
    // opened could not stay away. The prototype scrolls on the opening and not
    // on arrival, which is why the first render never scrolls either.
    // The rail opens on „teraz": the first row at or after it sits directly
    // under the kreska and the past is reached by scrolling up, which is what
    // the prototype does. Only on the first render and when the hour the axis
    // reads has moved — never on a poll, and never fighting the line below,
    // which answers an opening and nothing else.
    // …and it keeps positioning until every source has answered. The four feeds
    // arrive one at a time: position against the first of them and „teraz" is
    // the top row of a four-row list, so the rail is put at zero and the past
    // that lands a moment later pushes „teraz" back off the screen.
    // While the app is loading the rail is positioned on **every** render: the
    // four feeds land one at a time, and „teraz" is genuinely the first row
    // until the past arrives a moment later. The window is closed once, by the
    // timer below, and after that the rail is the resident's — a poll that
    // re-positioned it would be the defect above wearing another hat. A later
    // move of the axis's hour positions once more.
    const at = view.at;
    // `shown` only advances once the row was really there. A `sel` that names
    // something the ring does not hold has no row to scroll to; the widening
    // lands a moment later, and this must still read as an opening then
    // (§6's ruling, #225).
    const drew = view.selected == null || rowFor(list, view.selected) != null;
    if (view.selected && view.selected !== shown) {
      // An opening is a deliberate act and outranks the loading app: whoever
      // just opened a row wants to see that row, not „teraz". It also ends the
      // load window — from here the rail answers the resident and nobody else.
      scrollRowIntoView(list, view.selected);
      placedFor = at;
    } else if (!byTime) {
      // „teraz" is not where this list opens, so the rail is never placed on it.
      placedFor = at;
    } else if (placedFor === undefined) placeAtNow(list);
    else if (placedFor !== at) {
      placeAtNow(list);
      placedFor = at;
    }
    if (drew) shown = view.selected;
  }

  // The one subscription that draws this rail. It stands where three used to:
  // the snapshot, the view, and the centre — which moves on two clocks, the
  // address and the browser answering with a position (§5.8). A `tu=` change is
  // both of the last two and rebuilt the whole list twice, with two clocks and
  // two scroll restorations; it is one derivation and one render now (ADR-0016).
  // A late subscriber is handed what is standing, so no first paint is missed.
  derived.subscribe((next) => {
    state = next;
    render();
  });
  // The live half of a gesture, never through `derived.subscribe` (§1.1, rule
  // 2). The SETTLE is deliberately not a render: its one state write raises a
  // derivation of its own, and rendering here as well rebuilt the rail twice
  // for one thumb movement — §7.3's own mutation, and 130 ms instead of 62 on
  // the phone. The end of a gesture gives back the rows it hid, and drops the
  // FLIP's one-shot if no render took it: a settle that wrote nothing raises
  // none, and the flag would ride on to a poll ten seconds later.
  gesture.subscribe((live) => {
    if (live) return render();
    clearLive(list);
    queueMicrotask(() => void gesture.tookSettle());
  });
  // One last placement when the feeds have had their chance, and from then on
  // the rail stays where the resident leaves it.
  setTimeout(() => {
    if (viewState.get().order === 'czas') placeAtNow(list);
    placedFor = viewState.get().at;
  }, LOAD_MS);

  // What the resident was looking at, and putting it back after a re-wrap —
  // `./anchor.ts`'s, because it is anchoring and because `mountAxis` is at its
  // own line limit (ADR-0015).
  holdThroughResize(rail, list, kreska);
}
